import React, { useContext, useEffect, useMemo, useState } from 'react';
import { BaseTool, type IToolComponentProps, type IToolContext, type IToolManifest } from '../../core/tools/interfaces/ITool';
import { useDiagramStore } from '../../stores/diagramStore';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { UpdateNodePatchCommand } from '../../core/commands/nodes/UpdateNodePatchCommand';
import { resolveMediaSrc } from '../../domains/media/utils/resolveMediaSrc';
import { DiagramDocumentContext } from '../../contexts/DiagramDocumentContext';
import { useTranslation } from '../../hooks/useTranslation';

type PdfToolExecuteParams = {
  attrsPatch?: Record<string, unknown>;
};

const PdfPropertiesPanel: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const diagramDoc = useContext(DiagramDocumentContext);
  const selectionVersion = context.selectionSummary?.version;
  const selectedIds = useMemo(() => Array.from(context.selection), [context.selection, selectionVersion]);
  const nodes = useDiagramStore((s) => s.nodes);
  const selectedPdfs = useMemo(
    () => nodes.filter((node) => selectedIds.includes(node.id) && node.type === 'pdf'),
    [nodes, selectedIds],
  );

  const primary = selectedPdfs[0];
  const attrs = (primary?.attrs as Record<string, unknown> | undefined) ?? {};
  const [name, setName] = useState<string>(typeof attrs.name === 'string' ? attrs.name : '');
  const [description, setDescription] = useState<string>(typeof attrs.description === 'string' ? attrs.description : '');

  useEffect(() => {
    setName(typeof attrs.name === 'string' ? attrs.name : '');
    setDescription(typeof attrs.description === 'string' ? attrs.description : '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary?.id, selectionVersion]);

  if (selectedPdfs.length === 0) {
    return (
      <div className="px-4 py-3 text-xs text-muted-foreground">
        {t('pdfProperties.selectPrompt', { defaultValue: 'Select a PDF to edit properties.' })}
      </div>
    );
  }

  const objectKey = typeof attrs.objectKey === 'string' ? attrs.objectKey.trim() : '';
  const uploadState = attrs.uploadState === 'uploading' ? 'uploading' : 'ready';
  const canOpen = objectKey.length > 0 && uploadState === 'ready';

  const commitName = () => {
    const currentName = typeof attrs.name === 'string' ? attrs.name : '';
    if (name === currentName) return;
    onExecute({ attrsPatch: { name } });
  };

  const commitDescription = () => {
    const currentDescription = typeof attrs.description === 'string' ? attrs.description : '';
    if (description === currentDescription) return;
    onExecute({ attrsPatch: { description } });
  };

  return (
    <div className="px-4 py-3 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">{t('pdfProperties.title', { defaultValue: 'PDF' })}</h3>
        <p className="text-xs text-muted-foreground">
          {selectedPdfs.length > 1
            ? t('pdfProperties.editingMany', {
              defaultValue: 'Editing {{count}} selected PDFs',
              count: selectedPdfs.length,
            })
            : t('pdfProperties.editSingle', { defaultValue: 'Edit document metadata and open the uploaded file' })}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="pdf-name">{t('pdfProperties.nameLabel', { defaultValue: 'Name' })}</Label>
        <Input
          id="pdf-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={commitName}
          placeholder={t('pdfProperties.namePlaceholder', { defaultValue: 'Document name' })}
          data-testid="tid.pdf.properties.name"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="pdf-description">{t('pdfProperties.descriptionLabel', { defaultValue: 'Description' })}</Label>
        <Textarea
          id="pdf-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={commitDescription}
          placeholder={t('pdfProperties.descriptionPlaceholder', { defaultValue: 'Optional description' })}
          rows={3}
          data-testid="tid.pdf.properties.description"
        />
      </div>

      <Button
        type="button"
        variant="outline"
        disabled={!canOpen}
        onClick={() => {
          if (!canOpen) return;
          const href = resolveMediaSrc(objectKey, { diagramId: diagramDoc?.diagramId });
          window.open(href, '_blank', 'noopener,noreferrer');
        }}
        data-testid="tid.pdf.properties.open"
      >
        {t('pdfProperties.openButton', { defaultValue: 'Open PDF' })}
      </Button>
    </div>
  );
};

export class PdfPropertiesTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'pdf-properties',
    name: 'PDF',
    icon: 'file-text',
    category: 'style',
    description: 'Edit PDF metadata and open the uploaded file',
    version: '1.0.0',
    requiresSelection: true,
    minSelection: 1,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: false,
      statusBar: false,
    },
    applicability: {
      contextTypes: ['pdf'],
    },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  };

  private getSelectedPdfNodes(): Array<Record<string, unknown>> {
    if (!this.context) return [];
    return this.context
      .getSelectedShapes()
      .map((shape) => (shape as { data?: unknown }).data)
      .filter((node): node is Record<string, unknown> => Boolean(node && typeof node === 'object' && (node as { type?: unknown }).type === 'pdf'));
  }

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;
    const shapes = context.getSelectedShapes();
    return shapes.some((shape) => (shape as { data?: { type?: string } }).data?.type === 'pdf');
  }

  execute(params?: PdfToolExecuteParams) {
    const selectedNodes = this.getSelectedPdfNodes();
    if (selectedNodes.length === 0) return null;
    if (!params?.attrsPatch || Object.keys(params.attrsPatch).length === 0) return null;

    return selectedNodes.map((node) => {
      const existingAttrs = ((node.attrs as Record<string, unknown> | undefined) ?? {});
      return new UpdateNodePatchCommand(String(node.id), {
        attrs: {
          ...existingAttrs,
          ...params.attrsPatch,
        },
      });
    });
  }

  getPropertyPanelComponent() {
    return PdfPropertiesPanel;
  }
}
